import axios from 'axios';
import { SERVER_URL } from 'Constants';

const CARGO_API = `${SERVER_URL}/api/entity/CargoEntity`;

/**
 * A customer cargo profile and its characteristic delta against the cargo type's inherited set.
 * `addedCharacteristicIds` are characteristics added on top of inherited; `removedInheritedCharacteristicIds`
 * are inherited characteristics turned off. Effective set = (inherited ∪ added) \ removed.
 */
export interface CustomerCargoProfileDto {
	id: string;
	name: string;
	cargoTypeId: string;
	addedCharacteristicIds: string[];
	removedInheritedCharacteristicIds: string[];
}

export interface SaveCustomerCargoProfileRequest {
	/** Null when creating a new profile. */
	id?: string;
	name: string;
	cargoTypeId: string;
	addedCharacteristicIds: string[];
	removedInheritedCharacteristicIds: string[];
}

/**
 * Customer cargo profiles pre-define a customer's cargo characteristics for a given cargo type. When a
 * booking for that customer selects the cargo type, the profile's characteristics are applied
 * automatically. Staff-managed; these endpoints are staff-only on the server.
 */
export function getCargoProfiles(userId: string): Promise<CustomerCargoProfileDto[]> {
	return axios.get(`${CARGO_API}/cargo-profiles/user/${userId}`).then(x => x.data);
}

export function saveCargoProfile(
	userId: string,
	request: SaveCustomerCargoProfileRequest,
): Promise<CustomerCargoProfileDto> {
	return axios.post(`${CARGO_API}/cargo-profiles/user/${userId}`, request).then(x => x.data);
}

export function deleteCargoProfile(profileId: string): Promise<void> {
	return axios.delete(`${CARGO_API}/cargo-profiles/${profileId}`).then(() => undefined);
}

/**
 * The cargo type ids the customer has a profile for. When the selected vehicle is one of these, its
 * characteristics are implied by the profile and the booker does not edit them in the flow.
 */
export function getProfileCargoTypeIds(userId: string): Promise<string[]> {
	return getCargoProfiles(userId).then(profiles => profiles.map(p => p.cargoTypeId));
}
